import { z } from 'zod';
import type { PermissionKey, Scope } from './permissions.js';
import * as c from './schemas/common.js';
import * as id from './schemas/identity.js';
import * as t from './schemas/time.js';
import * as w from './schemas/work.js';
import * as m from './schemas/comms.js';
import * as s from './schemas/system.js';
import * as sup from './schemas/support.js';

/**
 * THE ENDPOINT REGISTRY — the single source of truth for the HTTP surface.
 *
 * Every endpoint declares its method, path, permission key and scope, and its request
 * and response schemas. Four things are derived from this one list rather than written
 * three times and kept in step by hand:
 *
 *   1. the backend route wiring and `validate()` schemas
 *   2. the frontend typed API client
 *   3. the mock (MSW) handlers the UI is built against
 *   4. the coverage tests — every endpoint has a permission key that exists in the
 *      registry, and every matrix cell is reachable
 *
 * This is why Phase 2 is the linchpin of a frontend-first build: the UI can be built
 * months ahead of the API without the two drifting, because a mismatch is a COMPILE
 * ERROR rather than a bug found during integration.
 */

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface EndpointContract {
  method: HttpMethod;
  /** Express-style path relative to `/api/v1`, e.g. `/employees/:id`. */
  path: string;
  summary: string;
  /** `null` means genuinely public. Every other endpoint names a key. */
  permission: PermissionKey | null;
  /** The minimum scope required. Omitted when the key has only one scope. */
  scope?: Scope;
  /** False only for the handful of pre-authentication endpoints. */
  auth: boolean;
  request?: {
    body?: z.ZodTypeAny;
    query?: z.ZodTypeAny;
    params?: z.ZodTypeAny;
  };
  response: z.ZodTypeAny;
  /** Audit action name. Every mutation has one. */
  audit?: string;
  /** Overrides the global limiter. */
  rateLimit?: string;
  /** Multipart rather than JSON. */
  upload?: boolean;
  notes?: string;
}

const define = <T extends Record<string, EndpointContract>>(contracts: T): T => contracts;

// ── Health and app config ───────────────────────────────────────────────────────

const systemContracts = define({
  'health.get': {
    method: 'GET',
    path: '/health',
    summary: 'Liveness and database readiness',
    permission: null,
    auth: false,
    response: c.single(s.health),
    notes: 'Returns 503 when the database is unreachable. Leaks no internal detail.',
  },
  'app.config': {
    method: 'GET',
    path: '/app/config',
    summary: 'Public feature flags and the Web Push public key',
    permission: null,
    auth: false,
    response: c.single(s.appConfig),
  },
});

// ── Authentication ──────────────────────────────────────────────────────────────

const authContracts = define({
  'auth.login': {
    method: 'POST',
    path: '/auth/login',
    summary: 'Sign in and set the session cookies',
    permission: null,
    auth: false,
    request: { body: id.loginBody },
    response: c.single(id.sessionUser),
    rateLimit: '10 per 15 min per IP and per account',
    audit: 'auth.login',
    notes: 'Generic failure message, and a constant-time compare for unknown emails.',
  },
  'auth.refresh': {
    method: 'POST',
    path: '/auth/refresh',
    summary: 'Rotate the refresh token within the same family',
    permission: null,
    auth: false,
    response: c.ok,
    rateLimit: '30 per 15 min per user',
    notes:
      'CSRF-exempt: no readable token exists yet. Replaying a rotated token revokes the ENTIRE family.',
  },
  'auth.logout': {
    method: 'POST',
    path: '/auth/logout',
    summary: 'Revoke the session, optionally everywhere',
    permission: null,
    auth: true,
    request: { body: id.logoutBody },
    response: c.ok,
    audit: 'auth.logout',
  },
  'auth.me': {
    method: 'GET',
    path: '/auth/me',
    summary: 'The signed-in user with resolved permissions',
    permission: null,
    auth: true,
    response: c.single(id.sessionUser),
    notes: 'Read-only. Writes nothing on a GET.',
  },
  'auth.activationInfo': {
    method: 'GET',
    path: '/auth/activation-info',
    summary: 'Minimal detail for the activation screen',
    permission: null,
    auth: false,
    request: { query: id.activationInfoQuery },
    response: c.single(id.activationInfo),
    notes: 'The token travels in the query string and is redacted from logs.',
  },
  'auth.activate': {
    method: 'POST',
    path: '/auth/activate',
    summary: 'Set the initial password and activate the account',
    permission: null,
    auth: false,
    request: { body: id.activateBody },
    response: c.single(id.sessionUser),
    rateLimit: '10 per 15 min per IP',
    audit: 'auth.activate',
  },
  'auth.changePassword': {
    method: 'POST',
    path: '/auth/password/change',
    summary: 'Change your own password',
    permission: null,
    auth: true,
    request: { body: id.changePasswordBody },
    response: c.ok,
    audit: 'auth.passwordChanged',
    notes: 'Revokes every other session.',
  },
  'auth.forgotPassword': {
    method: 'POST',
    path: '/auth/password/forgot',
    summary: 'Request a reset link',
    permission: null,
    auth: false,
    request: { body: id.forgotPasswordBody },
    response: c.ok,
    audit: 'auth.passwordResetRequested',
    rateLimit: '10 per 15 min per IP',
    notes:
      'ALWAYS succeeds, whether or not the address exists — no account enumeration. ' +
      'Audited without an actor, so a burst of requests is visible as an attack.',
  },
  'auth.resetPassword': {
    method: 'POST',
    path: '/auth/password/reset',
    summary: 'Set a new password from a reset token',
    permission: null,
    auth: false,
    request: { body: id.resetPasswordBody },
    response: c.single(id.sessionUser),
    audit: 'auth.passwordReset',
    notes:
      'Signs the user in, and revokes every session that existed before the reset — ' +
      'including whoever else may have had one.',
  },
  'auth.sessions': {
    method: 'GET',
    path: '/auth/sessions',
    summary: 'Active sessions for this account',
    permission: null,
    auth: true,
    response: c.collection(id.sessionRecord),
  },
  'auth.revokeSession': {
    method: 'DELETE',
    path: '/auth/sessions/:id',
    summary: 'Revoke one session',
    permission: null,
    auth: true,
    request: { params: c.idParam },
    response: c.ok,
    audit: 'auth.sessionRevoked',
  },
});

// ── Employees and organization ──────────────────────────────────────────────────

const employeeContracts = define({
  'employees.list': {
    method: 'GET',
    path: '/employees',
    summary: 'Employee directory',
    permission: 'employees.read',
    scope: 'reports',
    auth: true,
    request: { query: id.employeeListQuery },
    response: c.paginated(id.employeeListItem),
    notes: 'Search uses the text index. No user input is ever compiled into a RegExp.',
  },
  'employees.get': {
    method: 'GET',
    path: '/employees/:id',
    summary: 'One employee',
    permission: 'employees.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.single(id.employee),
    notes: 'Returns 404 — not 403 — when out of scope, so existence is not confirmed.',
  },
  'employees.create': {
    method: 'POST',
    path: '/employees',
    summary: 'Create an employee and provision their account',
    permission: 'employees.create',
    auth: true,
    request: { body: id.createEmployeeBody },
    response: c.single(id.employeeCreated),
    audit: 'employee.created',
    notes: 'Transactional: employee + user + activation token + audit, or none of them.',
  },
  'employees.update': {
    method: 'PATCH',
    path: '/employees/:id',
    summary: 'Update an employee record',
    permission: 'employees.update',
    scope: 'own',
    auth: true,
    request: { params: c.idParam, body: id.updateEmployeeBody },
    response: c.single(id.employee),
    audit: 'employee.updated',
    notes:
      'Rank check enforced here. An actor may never modify a target of equal or higher rank — ' +
      'including through this generic path, which is where the check is usually missing.',
  },
  'employees.updateOwn': {
    method: 'PATCH',
    path: '/me',
    summary: 'Update your own profile',
    permission: 'employees.update',
    scope: 'own',
    auth: true,
    request: { body: id.updateOwnProfileBody },
    response: c.single(id.employee),
    audit: 'employee.selfUpdated',
  },
  'employees.setStatus': {
    method: 'PATCH',
    path: '/employees/:id/status',
    summary: 'Set employment status',
    permission: 'employees.setStatus',
    auth: true,
    request: { params: c.idParam, body: id.setEmployeeStatusBody },
    response: c.single(id.employee),
    audit: 'employee.statusChanged',
  },
  'employees.assignRole': {
    method: 'PATCH',
    path: '/employees/:id/role',
    summary: 'Change the account role',
    permission: 'employees.assignRole',
    auth: true,
    request: { params: c.idParam, body: id.assignRoleBody },
    response: c.single(id.employee),
    audit: 'employee.roleChanged',
    notes: 'Writes users.role — the single source of role truth. No shadow copy exists.',
  },
  'employees.assignManager': {
    method: 'PATCH',
    path: '/employees/:id/manager',
    summary: 'Change the reporting manager',
    permission: 'employees.assignManager',
    auth: true,
    request: { params: c.idParam, body: id.assignManagerBody },
    response: c.single(id.employee),
    audit: 'employee.managerChanged',
    notes: 'Rejects a reporting cycle, direct or transitive.',
  },
  'employees.activateAccount': {
    method: 'POST',
    path: '/employees/:id/account/activate',
    summary: 'Re-enable the portal account',
    permission: 'employees.manageAccount',
    auth: true,
    request: { params: c.idParam },
    response: c.single(id.employee),
    audit: 'employee.accountActivated',
  },
  'employees.deactivateAccount': {
    method: 'POST',
    path: '/employees/:id/account/deactivate',
    summary: 'Disable the portal account',
    permission: 'employees.manageAccount',
    auth: true,
    request: { params: c.idParam },
    response: c.single(id.employee),
    audit: 'employee.accountDeactivated',
    notes: 'Revokes all sessions immediately. Cannot target the last active Super Admin.',
  },
  'employees.issueTemporaryPassword': {
    method: 'POST',
    path: '/employees/:id/account/temporary-password',
    summary: 'Set a new temporary sign-in password',
    permission: 'employees.manageAccount',
    auth: true,
    request: { params: c.idParam, body: id.issueTemporaryPasswordBody },
    response: c.single(id.temporaryPasswordIssued),
    audit: 'employee.temporaryPasswordIssued',
    notes:
      'Returns the password once. The account is activated with mustChangePassword set; all sessions are revoked.',
  },
  'employees.sendInvitation': {
    method: 'POST',
    path: '/employees/:id/account/invite',
    summary: 'Send or resend the activation invitation',
    permission: 'employees.create',
    auth: true,
    request: { params: c.idParam },
    response: c.ok,
    audit: 'employee.invited',
  },
  'employees.readSensitive': {
    method: 'GET',
    path: '/employees/:id/sensitive',
    summary: 'Unmasked bank and statutory identifiers',
    permission: 'employees.readSensitive',
    auth: true,
    request: { params: c.idParam },
    response: c.single(id.sensitiveFields),
    audit: 'employee.sensitiveRevealed',
    notes: 'Every call is audited. The CEO does not hold this permission.',
  },
  'employees.history': {
    method: 'GET',
    path: '/employees/:id/history',
    summary: 'Field-level change history',
    permission: 'employees.readHistory',
    scope: 'own',
    auth: true,
    request: { params: c.idParam, query: c.listQuery },
    response: c.paginated(id.employeeHistoryEntry),
  },
  'employees.reporting': {
    method: 'GET',
    path: '/employees/:id/reporting',
    summary: 'Manager chain and direct reports',
    permission: 'employees.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.single(id.reportingMeta),
  },
  'employees.hierarchy': {
    method: 'GET',
    path: '/employees/hierarchy',
    summary: 'Organization chart',
    permission: 'organization.readChart',
    auth: true,
    response: c.collection(id.hierarchyNode),
    notes: 'Directory fields only — readable by everyone, carries nothing sensitive.',
  },
  'employees.export': {
    method: 'GET',
    path: '/employees/export',
    summary: 'CSV export of the directory',
    permission: 'employees.export',
    scope: 'reports',
    auth: true,
    request: { query: id.employeeListQuery },
    response: c.empty,
    audit: 'employee.exported',
    rateLimit: '10 per hour per user',
    notes: 'Scope-filtered, and sensitive fields stay masked.',
  },
  'me.get': {
    method: 'GET',
    path: '/me',
    summary: 'Your own employee record',
    permission: 'employees.read',
    scope: 'own',
    auth: true,
    response: c.single(id.employee),
  },
  'myTeam.list': {
    method: 'GET',
    path: '/my-team',
    summary: 'Direct reports with a status summary',
    permission: 'team.read',
    scope: 'reports',
    auth: true,
    response: c.collection(s.myTeamMember),
  },
});

const departmentContracts = define({
  'departments.list': {
    method: 'GET',
    path: '/departments',
    summary: 'All departments',
    permission: 'departments.read',
    auth: true,
    request: { query: c.listQuery },
    response: c.paginated(id.department),
    notes: 'Counts are computed by aggregation. A GET never writes.',
  },
  'departments.create': {
    method: 'POST',
    path: '/departments',
    summary: 'Create a department',
    permission: 'departments.manage',
    auth: true,
    request: { body: id.createDepartmentBody },
    response: c.single(id.department),
    audit: 'department.created',
  },
  'departments.update': {
    method: 'PATCH',
    path: '/departments/:id',
    summary: 'Update a department',
    permission: 'departments.manage',
    auth: true,
    request: { params: c.idParam, body: id.updateDepartmentBody },
    response: c.single(id.department),
    audit: 'department.updated',
  },
  'departments.delete': {
    method: 'DELETE',
    path: '/departments/:id',
    summary: 'Archive a department',
    permission: 'departments.delete',
    auth: true,
    request: { params: c.idParam },
    response: c.ok,
    audit: 'department.archived',
    notes: 'Refused while active employees are assigned.',
  },
  'departments.assignHead': {
    method: 'PATCH',
    path: '/departments/:id/head',
    summary: 'Assign the department head',
    permission: 'departments.assignHead',
    auth: true,
    request: { params: c.idParam, body: id.assignDepartmentHeadBody },
    response: c.single(id.department),
    audit: 'department.headAssigned',
    notes: 'An organizational assignment. Grants department scope, not a system role.',
  },
  'workSchedules.list': {
    method: 'GET',
    path: '/work-schedules',
    summary: 'Working patterns',
    permission: 'attendance.configure',
    auth: true,
    response: c.collection(id.workSchedule),
  },
  'workSchedules.create': {
    method: 'POST',
    path: '/work-schedules',
    summary: 'Create a working pattern',
    permission: 'attendance.configure',
    auth: true,
    request: { body: id.createWorkScheduleBody },
    response: c.single(id.workSchedule),
    audit: 'workSchedule.created',
  },
});

// ── Attendance ──────────────────────────────────────────────────────────────────

const attendanceContracts = define({
  'attendance.punchIn': {
    method: 'POST',
    path: '/attendance/punch-in',
    summary: 'Start the working day',
    permission: 'attendance.punch',
    auth: true,
    request: { body: t.punchBody },
    response: c.single(t.attendanceDay),
    audit: 'attendance.punchedIn',
    notes:
      'Server-stamped. A partial unique index guarantees one open punch per employee; ' +
      'a same-day re-punch starts a NEW session and preserves the earlier one.',
  },
  'attendance.punchOut': {
    method: 'POST',
    path: '/attendance/punch-out',
    summary: 'End the working day',
    permission: 'attendance.punch',
    auth: true,
    request: { body: t.punchBody },
    response: c.single(t.attendanceDay),
    audit: 'attendance.punchedOut',
  },
  'attendance.breakStart': {
    method: 'POST',
    path: '/attendance/break/start',
    summary: 'Start a break',
    permission: 'attendance.punch',
    auth: true,
    request: { body: t.punchBody },
    response: c.single(t.attendanceDay),
    audit: 'attendance.breakStarted',
  },
  'attendance.breakEnd': {
    method: 'POST',
    path: '/attendance/break/end',
    summary: 'End a break',
    permission: 'attendance.punch',
    auth: true,
    request: { body: t.punchBody },
    response: c.single(t.attendanceDay),
    audit: 'attendance.breakEnded',
  },
  'attendance.today': {
    method: 'GET',
    path: '/attendance/me/today',
    summary: "Today's punch state for the widget",
    permission: 'attendance.read',
    scope: 'own',
    auth: true,
    response: c.single(t.attendanceDay.nullable()),
  },
  'attendance.list': {
    method: 'GET',
    path: '/attendance',
    summary: 'Attendance days within scope',
    permission: 'attendance.read',
    scope: 'own',
    auth: true,
    request: { query: t.attendanceListQuery },
    response: c.paginated(t.attendanceDay),
  },
  'attendance.day': {
    method: 'GET',
    path: '/attendance/:id',
    summary: 'One attendance day with its event log',
    permission: 'attendance.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.single(t.attendanceDay),
  },
  'attendance.corrections.list': {
    method: 'GET',
    path: '/attendance/corrections',
    summary: 'Correction requests',
    permission: 'attendance.read',
    scope: 'own',
    auth: true,
    request: { query: c.listQuery },
    response: c.paginated(t.attendanceCorrection),
  },
  'attendance.corrections.create': {
    method: 'POST',
    path: '/attendance/corrections',
    summary: 'Request a correction',
    permission: 'attendance.requestCorrection',
    auth: true,
    request: { body: t.createCorrectionBody },
    response: c.single(t.attendanceCorrection),
    audit: 'attendance.correctionRequested',
    notes: 'Routed through the generic approval engine, like every other request type.',
  },
  'attendance.correctDirect': {
    method: 'POST',
    path: '/attendance/corrections/:id/apply',
    summary: 'Apply a correction directly',
    permission: 'attendance.correctDirect',
    auth: true,
    request: { params: c.idParam },
    response: c.single(t.attendanceDay),
    audit: 'attendance.correctedDirectly',
    notes: 'Audited with both the previous and the new value.',
  },
  'attendance.export': {
    method: 'GET',
    path: '/attendance/export',
    summary: 'CSV export',
    permission: 'attendance.export',
    scope: 'own',
    auth: true,
    request: { query: s.exportQuery },
    response: c.empty,
    audit: 'attendance.exported',
    rateLimit: '10 per hour per user',
  },
  'attendance.policy.get': {
    method: 'GET',
    path: '/settings/attendance-policy',
    summary: 'Attendance policy',
    permission: 'attendance.configure',
    auth: true,
    response: c.single(t.attendancePolicy),
  },
  'attendance.policy.update': {
    method: 'PATCH',
    path: '/settings/attendance-policy',
    summary: 'Update attendance policy',
    permission: 'attendance.configure',
    auth: true,
    request: { body: t.attendancePolicy.partial() },
    response: c.single(t.attendancePolicy),
    audit: 'settings.attendancePolicyUpdated',
    notes: 'Every setting here has a job or rule behind it. Inert toggles are not shipped.',
  },
});

// ── Leave ───────────────────────────────────────────────────────────────────────

const leaveContracts = define({
  'leaveTypes.list': {
    method: 'GET',
    path: '/leave-types',
    summary: 'Configured leave types',
    permission: 'leave.read',
    scope: 'own',
    auth: true,
    request: { query: t.leaveTypeListQuery },
    response: c.collection(t.leaveType),
  },
  'leaveTypes.create': {
    method: 'POST',
    path: '/leave-types',
    summary: 'Create a leave type',
    permission: 'leave.configure',
    auth: true,
    request: { body: t.createLeaveTypeBody },
    response: c.single(t.leaveType),
    audit: 'leaveType.created',
  },
  'leaveTypes.update': {
    method: 'PATCH',
    path: '/leave-types/:id',
    summary: 'Update a leave type',
    permission: 'leave.configure',
    auth: true,
    request: { params: c.idParam, body: t.updateLeaveTypeBody },
    response: c.single(t.leaveType),
    audit: 'leaveType.updated',
  },
  'leaveTypes.delete': {
    method: 'DELETE',
    path: '/leave-types/:id',
    summary: 'Archive a leave type',
    permission: 'leave.configure',
    auth: true,
    request: { params: c.idParam },
    response: c.ok,
    audit: 'leaveType.archived',
    notes: 'Soft-archive only. Existing balances and requests keep their type.',
  },
  'leave.previewDays': {
    method: 'POST',
    path: '/leave/preview-days',
    summary: 'Day count with its full breakdown',
    permission: 'leave.create',
    auth: true,
    request: { body: t.previewLeaveDaysBody },
    response: c.single(t.leaveDaysPreview),
    notes: "Returns the breakdown, not just a number — a disputed count has to be explainable.",
  },
  'leave.list': {
    method: 'GET',
    path: '/leave',
    summary: 'Leave requests within scope',
    permission: 'leave.read',
    scope: 'own',
    auth: true,
    request: { query: t.leaveListQuery },
    response: c.paginated(t.leaveRequest),
  },
  'leave.get': {
    method: 'GET',
    path: '/leave/:id',
    summary: 'One leave request',
    permission: 'leave.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.single(t.leaveRequest),
  },
  'leave.apply': {
    method: 'POST',
    path: '/leave',
    summary: 'Apply for leave',
    permission: 'leave.create',
    auth: true,
    request: { body: t.applyForLeaveBody },
    response: c.single(t.leaveRequest),
    audit: 'leave.applied',
    notes:
      'Overlap is rejected by a database constraint, not only in code. A request crossing ' +
      'the leave-year boundary debits each year once via leaveYearSplit.',
  },
  'leave.uploadProof': {
    method: 'POST',
    path: '/leave/proof-files',
    summary: 'Upload supporting proof for a leave application',
    permission: 'leave.create',
    auth: true,
    upload: true,
    response: c.single(m.announcementAttachment),
    // leave.service.ts#uploadProofFile already writes this row. The declaration was
    // missing, so the registry under-reported audit coverage rather than the server
    // failing to audit. A leave proof is often a medical certificate, so the row
    // matters.
    audit: 'leave.proofUploaded',
    rateLimit: '20 per hour per user',
  },
  'leave.cancel': {
    method: 'POST',
    path: '/leave/:id/cancel',
    summary: 'Cancel or withdraw a request',
    permission: 'leave.cancel',
    auth: true,
    request: { params: c.idParam },
    response: c.single(t.leaveRequest),
    audit: 'leave.cancelled',
  },
  'leave.balance': {
    method: 'GET',
    path: '/leave/balance',
    summary: 'Balances by type and leave year',
    permission: 'leave.readBalance',
    scope: 'own',
    auth: true,
    request: { query: z.object({ employeeId: c.objectId.optional(), leaveYear: z.coerce.number().int().optional() }) },
    response: c.collection(t.leaveBalance),
  },
  'leave.ledger': {
    method: 'GET',
    path: '/leave/balance/ledger',
    summary: 'Every balance movement',
    permission: 'leave.readBalance',
    scope: 'own',
    auth: true,
    request: { query: c.listQuery },
    response: c.paginated(t.leaveLedgerEntry),
    notes: 'A balance without a ledger is unauditable.',
  },
  'leave.adjustBalance': {
    method: 'POST',
    path: '/leave/balance/adjust',
    summary: 'Manually adjust a balance',
    permission: 'leave.adjustBalance',
    auth: true,
    request: { body: t.adjustBalanceBody },
    response: c.single(t.leaveBalance),
    audit: 'leave.balanceAdjusted',
  },
  'leave.calendar': {
    method: 'GET',
    path: '/leave/calendar',
    summary: 'Leave calendar within scope',
    permission: 'leave.read',
    scope: 'own',
    auth: true,
    request: { query: t.leaveCalendarQuery },
    response: c.collection(t.leaveCalendarEntry),
  },
  'leave.proof': {
    method: 'GET',
    path: '/leave/:id/proof',
    summary: 'Download the proof attachment',
    permission: 'leave.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.empty,
    audit: 'leave.proofDownloaded',
    notes:
      'Legacy single-file download (first attachment). Prefer leave.downloadAttachment. ' +
      'Applicant, assigned approver and authorized HR only.',
  },
  'leave.downloadAttachment': {
    method: 'GET',
    path: '/leave/:id/attachment-files/:fileId/download',
    summary: 'Download a leave request attachment',
    permission: 'leave.read',
    scope: 'own',
    auth: true,
    request: { params: z.object({ id: c.objectId, fileId: c.objectId }) },
    response: c.empty,
    audit: 'leave.attachmentDownloaded',
    notes: 'Applicant, assigned approver and authorized HR only. Streamed, never a public path.',
  },
  'leave.export': {
    method: 'GET',
    path: '/leave/export',
    summary: 'CSV export',
    permission: 'leave.export',
    scope: 'own',
    auth: true,
    request: { query: s.exportQuery },
    response: c.empty,
    audit: 'leave.exported',
    rateLimit: '10 per hour per user',
  },
});

// ── Approvals ───────────────────────────────────────────────────────────────────

const approvalContracts = define({
  'approvals.list': {
    method: 'GET',
    path: '/approvals',
    summary: 'The approval inbox, across every request type',
    permission: 'approvals.read',
    scope: 'own',
    auth: true,
    request: { query: w.approvalListQuery },
    response: c.paginated(w.approvalRequest),
    notes: 'One inbox over every workflow — approvers should not hunt through three modules.',
  },
  'approvals.get': {
    method: 'GET',
    path: '/approvals/:id',
    summary: 'A request with its full decision history',
    permission: 'approvals.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.single(w.approvalRequest),
  },
  'approvals.decide': {
    method: 'POST',
    path: '/approvals/:id/decide',
    summary: 'Approve or reject the active stage',
    permission: 'approvals.decide',
    scope: 'assigned',
    auth: true,
    request: { params: c.idParam, body: w.decideApprovalBody },
    response: c.single(w.approvalRequest),
    audit: 'approval.decided',
    notes:
      'Must be the resolved approver for the ACTIVE stage. Self-approval is impossible. ' +
      'Optimistic concurrency means two simultaneous decisions cannot both commit.',
  },
  'approvals.override': {
    method: 'POST',
    path: '/approvals/:id/override',
    summary: 'Administrative override',
    permission: 'approvals.reassign',
    auth: true,
    request: { params: c.idParam, body: w.overrideApprovalBody },
    response: c.single(w.approvalRequest),
    audit: 'approval.overridden',
    notes: 'A DISTINCT, separately audited action. An elevated role is never an implicit approver.',
  },
  'approvals.reassign': {
    method: 'POST',
    path: '/approvals/:id/reassign',
    summary: 'Reassign a stage to another approver',
    permission: 'approvals.reassign',
    auth: true,
    request: { params: c.idParam, body: w.reassignApprovalBody },
    response: c.single(w.approvalRequest),
    audit: 'approval.reassigned',
  },
  'approvals.chains.get': {
    method: 'GET',
    path: '/settings/approval-chains',
    summary: 'Configured approval chains',
    permission: 'approvals.configure',
    auth: true,
    response: c.single(w.approvalChainsBody),
  },
  'approvals.chains.update': {
    method: 'PUT',
    path: '/settings/approval-chains',
    summary: 'Replace the approval chain configuration',
    permission: 'approvals.configure',
    auth: true,
    request: { body: w.approvalChainsBody },
    response: c.single(w.approvalChainsBody),
    audit: 'settings.approvalChainsUpdated',
    notes: 'Never retroactive — in-flight requests carry their own chain snapshot.',
  },
});

// ── Tasks and work reports ──────────────────────────────────────────────────────

const taskContracts = define({
  'tasks.list': {
    method: 'GET',
    path: '/tasks',
    summary: 'Tasks within scope',
    permission: 'tasks.read',
    scope: 'own',
    auth: true,
    request: { query: w.taskListQuery },
    response: c.paginated(w.task),
    notes: 'Overdue is derived at read time, never a stored flag that goes stale.',
  },
  'tasks.board': {
    method: 'GET',
    path: '/tasks/board',
    summary: 'Tasks grouped by status',
    permission: 'tasks.read',
    scope: 'own',
    auth: true,
    request: { query: w.taskListQuery },
    response: c.collection(w.taskBoardColumn),
  },
  'tasks.get': {
    method: 'GET',
    path: '/tasks/:id',
    summary: 'One task',
    permission: 'tasks.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.single(w.task),
  },
  'tasks.create': {
    method: 'POST',
    path: '/tasks',
    summary: 'Create a task',
    permission: 'tasks.create',
    scope: 'own',
    auth: true,
    request: { body: w.createTaskBody },
    response: c.single(w.task),
    audit: 'task.created',
    notes: 'At `own` scope an employee may assign only to themselves.',
  },
  'tasks.update': {
    method: 'PATCH',
    path: '/tasks/:id',
    summary: 'Update a task',
    permission: 'tasks.update',
    scope: 'own',
    auth: true,
    request: { params: c.idParam, body: w.updateTaskBody },
    response: c.single(w.task),
    audit: 'task.updated',
    notes: 'Status transitions are validated against the state machine.',
  },
  'tasks.reassign': {
    method: 'PATCH',
    path: '/tasks/:id/assignee',
    summary: 'Reassign a task',
    permission: 'tasks.assign',
    scope: 'reports',
    auth: true,
    request: { params: c.idParam, body: w.reassignTaskBody },
    response: c.single(w.task),
    audit: 'task.reassigned',
    notes: 'Notifies both the previous and the new assignee.',
  },
  'tasks.delete': {
    method: 'DELETE',
    path: '/tasks/:id',
    summary: 'Soft-delete a task',
    permission: 'tasks.delete',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.ok,
    audit: 'task.deleted',
  },
  'tasks.comments.list': {
    method: 'GET',
    path: '/tasks/:id/comments',
    summary: 'Task comments',
    permission: 'tasks.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam, query: c.listQuery },
    response: c.paginated(w.taskComment),
  },
  'tasks.comments.create': {
    method: 'POST',
    path: '/tasks/:id/comments',
    summary: 'Comment on a task',
    permission: 'tasks.comment',
    scope: 'own',
    auth: true,
    request: { params: c.idParam, body: w.createTaskCommentBody },
    response: c.single(w.taskComment),
    audit: 'task.commented',
    notes: 'Rich text is sanitized server-side on write.',
  },
});

const workReportContracts = define({
  'workReports.list': {
    method: 'GET',
    path: '/work-reports',
    summary: 'Work reports within scope',
    permission: 'workReports.read',
    scope: 'own',
    auth: true,
    request: { query: w.workReportListQuery },
    response: c.paginated(w.workReport),
  },
  'workReports.get': {
    method: 'GET',
    path: '/work-reports/:id',
    summary: 'One report with its revision history',
    permission: 'workReports.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.single(w.workReport),
  },
  'workReports.create': {
    method: 'POST',
    path: '/work-reports',
    summary: 'Create a draft report',
    permission: 'workReports.create',
    auth: true,
    request: { body: w.createWorkReportBody },
    response: c.single(w.workReport),
    audit: 'workReport.created',
  },
  'workReports.update': {
    method: 'PATCH',
    path: '/work-reports/:id',
    summary: 'Edit a draft or a report with changes requested',
    permission: 'workReports.update',
    auth: true,
    request: { params: c.idParam, body: w.updateWorkReportBody },
    response: c.single(w.workReport),
    audit: 'workReport.updated',
    notes: 'A submitted report is locked. Editing after review creates a NEW revision.',
  },
  'workReports.submit': {
    method: 'POST',
    path: '/work-reports/:id/submit',
    summary: 'Submit for review',
    permission: 'workReports.create',
    auth: true,
    request: { params: c.idParam },
    response: c.single(w.workReport),
    audit: 'workReport.submitted',
    notes:
      'One non-draft report per employee per date, enforced by a unique index. Creates an ' +
      'approval request and emits workReport.submitted.',
  },
  'workReports.export': {
    method: 'GET',
    path: '/work-reports/export',
    summary: 'CSV export',
    permission: 'workReports.export',
    scope: 'own',
    auth: true,
    request: { query: s.exportQuery },
    response: c.empty,
    audit: 'workReport.exported',
    rateLimit: '10 per hour per user',
  },
});

// ── Work sessions ───────────────────────────────────────────────────────────────

const workSessionContracts = define({
  'workSessions.current': {
    method: 'GET',
    path: '/work-sessions/current',
    summary: 'The active session, if any',
    permission: 'workSessions.read',
    scope: 'own',
    auth: true,
    response: c.single(t.workSession.nullable()),
    notes: 'Recovery after a restart reads from HERE, not from local state.',
  },
  'workSessions.start': {
    method: 'POST',
    path: '/work-sessions',
    summary: 'Start a session',
    permission: 'workSessions.control',
    auth: true,
    request: { body: t.startWorkSessionBody },
    response: c.single(t.workSession),
    audit: 'workSession.started',
    notes: 'One active session per employee, enforced by a partial unique index.',
  },
  'workSessions.event': {
    method: 'POST',
    path: '/work-sessions/:id/events',
    summary: 'Append a transition',
    permission: 'workSessions.control',
    auth: true,
    request: { params: c.idParam, body: t.workSessionEventBody },
    response: c.single(t.workSessionEventResult),
    notes:
      'The idempotency key is uniquely indexed: a retry or an offline replay returns the ' +
      'original result and can never double-count.',
  },
  'workSessions.eventBatch': {
    method: 'POST',
    path: '/work-sessions/:id/events/batch',
    summary: 'Replay queued offline events',
    permission: 'workSessions.control',
    auth: true,
    request: { params: c.idParam, body: t.workSessionEventBatchBody },
    response: c.collection(t.workSessionEventResult),
    rateLimit: '240 per hour per user',
  },
  'workSessions.heartbeat': {
    method: 'POST',
    path: '/work-sessions/:id/heartbeat',
    summary: '60-second heartbeat',
    permission: 'workSessions.control',
    auth: true,
    request: { params: c.idParam, body: t.heartbeatBody },
    response: c.ok,
    rateLimit: '240 per hour per user',
    notes: 'Records client clock skew. Skew never enters duration arithmetic.',
  },
  'workSessions.list': {
    method: 'GET',
    path: '/work-sessions',
    summary: 'Session history within scope',
    permission: 'workSessions.read',
    scope: 'own',
    auth: true,
    request: { query: t.workSessionListQuery },
    response: c.paginated(t.workSession),
  },
  'workSessions.get': {
    method: 'GET',
    path: '/work-sessions/:id',
    summary: 'One session',
    permission: 'workSessions.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.single(t.workSession),
  },
});

// ── Notifications and announcements ─────────────────────────────────────────────

const notificationContracts = define({
  'notifications.list': {
    method: 'GET',
    path: '/notifications',
    summary: 'Your notifications',
    permission: 'notifications.read',
    auth: true,
    request: { query: m.notificationListQuery },
    response: c.paginated(m.notification),
    notes:
      'Served `private, no-store` with NO ETag: a 304 on a conditional request is how an ' +
      'unread badge goes stale.',
  },
  'notifications.unreadCount': {
    method: 'GET',
    path: '/notifications/unread-count',
    summary: 'Unread count',
    permission: 'notifications.read',
    auth: true,
    response: c.single(m.unreadCount),
  },
  'notifications.markRead': {
    method: 'PATCH',
    path: '/notifications/:id/read',
    summary: 'Mark one as read',
    permission: 'notifications.read',
    auth: true,
    request: { params: c.idParam },
    response: c.single(m.unreadCount),
    notes: 'Returns the authoritative count, so the client never decrements locally.',
  },
  'notifications.dismiss': {
    method: 'PATCH',
    path: '/notifications/:id/dismiss',
    summary: 'Dismiss one',
    permission: 'notifications.read',
    auth: true,
    request: { params: c.idParam },
    response: c.single(m.unreadCount),
  },
  'notifications.markAllRead': {
    method: 'PATCH',
    path: '/notifications/read-all',
    summary: 'Mark everything read',
    permission: 'notifications.read',
    auth: true,
    response: c.single(m.unreadCount),
  },
  'notifications.preferences.get': {
    method: 'GET',
    path: '/notifications/preferences',
    summary: 'Your notification preferences',
    permission: 'notifications.configure',
    auth: true,
    response: c.single(m.notificationPreferences),
  },
  'notifications.preferences.update': {
    method: 'PATCH',
    path: '/notifications/preferences',
    summary: 'Update your preferences',
    permission: 'notifications.configure',
    auth: true,
    request: { body: m.updateNotificationPreferencesBody },
    response: c.single(m.notificationPreferences),
    audit: 'notifications.preferencesUpdated',
  },
  'notifications.streamTicket': {
    method: 'POST',
    path: '/notifications/stream-ticket',
    summary: 'Single-use ticket for the SSE stream',
    permission: 'notifications.read',
    auth: true,
    response: c.single(m.streamTicket),
    rateLimit: '20 per min',
    notes: 'EventSource cannot set headers, hence a ticket. It is redacted from logs.',
  },
  'notifications.stream': {
    method: 'GET',
    path: '/notifications/stream',
    summary: 'Server-Sent Events stream',
    permission: 'notifications.read',
    auth: true,
    response: c.empty,
    notes: 'text/event-stream. Nginx must disable buffering on this route.',
  },
  'notifications.push.subscribe': {
    method: 'POST',
    path: '/notifications/push/subscription',
    summary: 'Register a Web Push subscription',
    permission: 'notifications.configure',
    auth: true,
    request: { body: m.pushSubscriptionBody },
    response: c.ok,
    notes: 'Re-registering an endpoint owned by another user deletes and reinserts it.',
  },
  'notifications.push.unsubscribe': {
    method: 'DELETE',
    path: '/notifications/push/subscription',
    summary: 'Remove a push subscription',
    permission: 'notifications.configure',
    auth: true,
    response: c.ok,
  },
  'notifications.policy.get': {
    method: 'GET',
    path: '/settings/notification-policy',
    summary: 'Event × channel policy matrix',
    permission: 'notifications.configurePolicy',
    auth: true,
    response: c.single(m.notificationPolicyBody),
  },
  'notifications.policy.update': {
    method: 'PUT',
    path: '/settings/notification-policy',
    summary: 'Replace the policy matrix',
    permission: 'notifications.configurePolicy',
    auth: true,
    request: { body: m.notificationPolicyBody },
    response: c.single(m.notificationPolicyBody),
    audit: 'settings.notificationPolicyUpdated',
  },
  'notifications.deliveryLog': {
    method: 'GET',
    path: '/notifications/delivery-log',
    summary: 'Delivery diagnostics',
    permission: 'audit.read',
    auth: true,
    request: { query: c.listQuery },
    response: c.paginated(m.deliveryLogEntry),
  },
});

const announcementContracts = define({
  'announcements.list': {
    method: 'GET',
    path: '/announcements',
    summary: 'Announcements you are an audience for',
    permission: 'announcements.read',
    scope: 'audience',
    auth: true,
    request: { query: m.announcementListQuery },
    response: c.paginated(m.announcement),
    notes: 'Audience filtering happens server-side.',
  },
  'announcements.get': {
    method: 'GET',
    path: '/announcements/:id',
    summary: 'One announcement',
    permission: 'announcements.read',
    scope: 'audience',
    auth: true,
    request: { params: c.idParam },
    response: c.single(m.announcement),
    notes: '404 when outside the audience.',
  },
  'announcements.create': {
    method: 'POST',
    path: '/announcements',
    summary: 'Create a draft or scheduled announcement',
    permission: 'announcements.manage',
    auth: true,
    request: { body: m.createAnnouncementBody },
    response: c.single(m.announcement),
    audit: 'announcement.created',
  },
  'announcements.update': {
    method: 'PATCH',
    path: '/announcements/:id',
    summary: 'Update an announcement',
    permission: 'announcements.manage',
    auth: true,
    request: { params: c.idParam, body: m.updateAnnouncementBody },
    response: c.single(m.announcement),
    audit: 'announcement.updated',
  },
  'announcements.publish': {
    method: 'POST',
    path: '/announcements/:id/publish',
    summary: 'Publish immediately',
    permission: 'announcements.manage',
    auth: true,
    request: { params: c.idParam },
    response: c.single(m.announcement),
    audit: 'announcement.published',
    notes: 'Notifies exactly the target audience, once per recipient.',
  },
  'announcements.acknowledge': {
    method: 'POST',
    path: '/announcements/:id/acknowledge',
    summary: 'Acknowledge an announcement',
    permission: 'announcements.read',
    scope: 'audience',
    auth: true,
    request: { params: c.idParam },
    response: c.ok,
  },
  'announcements.acknowledgements': {
    method: 'GET',
    path: '/announcements/:id/acknowledgements',
    summary: 'Who has read and acknowledged',
    permission: 'announcements.readAcks',
    auth: true,
    request: { params: c.idParam, query: c.listQuery },
    response: c.paginated(m.acknowledgementRow),
    notes: 'Author and HR only. Blanket read tracking is not enabled by default.',
  },
  'announcements.react': {
    method: 'PUT',
    path: '/announcements/:id/reaction',
    summary: 'React to an announcement',
    permission: 'announcements.read',
    scope: 'audience',
    auth: true,
    request: { params: c.idParam, body: m.setAnnouncementReactionBody },
    response: c.single(m.announcement),
  },
  'announcements.uploadAttachment': {
    method: 'POST',
    path: '/announcements/attachment-files',
    summary: 'Upload a file to attach to an announcement',
    permission: 'announcements.manage',
    auth: true,
    upload: true,
    response: c.single(m.announcementAttachment),
    // Written by announcement.service.ts#uploadAttachmentFile; see leave.uploadProof.
    audit: 'announcement.attachmentUploaded',
    rateLimit: '20 per hour per user',
  },
  'announcements.downloadAttachment': {
    method: 'GET',
    path: '/announcements/attachment-files/:fileId/download',
    summary: 'Download an announcement attachment',
    permission: 'announcements.read',
    scope: 'audience',
    auth: true,
    request: { params: z.object({ fileId: c.objectId }) },
    response: c.empty,
    audit: 'announcement.attachmentDownloaded',
  },
});

// ── Documents, onboarding, holidays ─────────────────────────────────────────────

const recordContracts = define({
  'documents.checklist': {
    method: 'GET',
    path: '/documents/employees/:id',
    summary: "An employee's document checklist",
    permission: 'documents.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.single(s.documentChecklist),
  },
  'documents.list': {
    method: 'GET',
    path: '/documents',
    summary: 'Documents across the organization',
    permission: 'documents.manage',
    auth: true,
    request: { query: s.documentListQuery },
    response: c.paginated(s.employeeDocument),
  },
  'documents.upload': {
    method: 'POST',
    path: '/documents/:id/upload',
    summary: 'Upload or replace a document',
    permission: 'documents.upload',
    scope: 'own',
    auth: true,
    upload: true,
    request: { params: c.idParam, body: s.uploadDocumentBody },
    response: c.single(s.employeeDocument),
    audit: 'document.uploaded',
    rateLimit: '20 per hour per user',
    notes:
      'Authorization runs BEFORE the file is written. Type is validated by content ' +
      'inspection; SVG is rejected; the stored name is generated.',
  },
  'documents.collect': {
    method: 'PATCH',
    path: '/documents/:id/collect',
    summary: 'Mark a document collected',
    permission: 'documents.manage',
    auth: true,
    request: { params: c.idParam },
    response: c.single(s.employeeDocument),
    audit: 'document.collected',
  },
  'documents.verify': {
    method: 'POST',
    path: '/documents/:id/verify',
    summary: 'Verify a document',
    permission: 'documents.manage',
    auth: true,
    request: { params: c.idParam, body: s.verifyDocumentBody },
    response: c.single(s.employeeDocument),
    audit: 'document.verified',
  },
  'documents.reject': {
    method: 'POST',
    path: '/documents/:id/reject',
    summary: 'Reject a document with a reason',
    permission: 'documents.manage',
    auth: true,
    request: { params: c.idParam, body: s.rejectDocumentBody },
    response: c.single(s.employeeDocument),
    audit: 'document.rejected',
  },
  'documents.download': {
    method: 'GET',
    path: '/documents/:id/download',
    summary: 'Download a document',
    permission: 'documents.download',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.empty,
    audit: 'document.downloaded',
    notes: 'Permission re-checked per request and streamed. Never served from a public path.',
  },
  'onboarding.list': {
    method: 'GET',
    path: '/onboarding',
    summary: 'Onboarding progress',
    permission: 'onboarding.read',
    scope: 'own',
    auth: true,
    request: { query: c.listQuery },
    response: c.paginated(s.onboardingChecklist),
  },
  'onboarding.get': {
    method: 'GET',
    path: '/onboarding/:id',
    summary: "One employee's checklist",
    permission: 'onboarding.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.single(s.onboardingChecklist),
  },
  'onboarding.updateItem': {
    method: 'PATCH',
    path: '/onboarding/:id/items/:code',
    summary: 'Complete or reopen a checklist item',
    permission: 'onboarding.manage',
    auth: true,
    request: {
      params: z.object({ id: c.objectId, code: z.string().max(60) }),
      body: s.updateOnboardingItemBody,
    },
    response: c.single(s.onboardingChecklist),
    audit: 'onboarding.itemUpdated',
  },
  'onboarding.confirmProbation': {
    method: 'POST',
    path: '/employees/:id/probation/confirm',
    summary: 'Confirm probation',
    permission: 'onboarding.manage',
    auth: true,
    request: { params: c.idParam, body: s.confirmProbationBody },
    response: c.single(id.employee),
    audit: 'employee.probationConfirmed',
  },
  'holidays.list': {
    method: 'GET',
    path: '/holidays',
    summary: 'Holiday calendar',
    permission: 'holidays.read',
    auth: true,
    request: { query: t.holidayListQuery },
    response: c.collection(t.holiday),
  },
  'holidays.create': {
    method: 'POST',
    path: '/holidays',
    summary: 'Add a holiday',
    permission: 'holidays.manage',
    auth: true,
    request: { body: t.createHolidayBody },
    response: c.single(t.holiday),
    audit: 'holiday.created',
  },
  'holidays.update': {
    method: 'PATCH',
    path: '/holidays/:id',
    summary: 'Update a holiday',
    permission: 'holidays.manage',
    auth: true,
    request: { params: c.idParam, body: t.createHolidayBody.partial() },
    response: c.single(t.holiday),
    audit: 'holiday.updated',
  },
  'holidays.delete': {
    method: 'DELETE',
    path: '/holidays/:id',
    summary: 'Remove a holiday',
    permission: 'holidays.manage',
    auth: true,
    request: { params: c.idParam },
    response: c.ok,
    audit: 'holiday.deleted',
  },
});

// ── Analytics, settings, audit ──────────────────────────────────────────────────

const adminContracts = define({
  'dashboard.me': {
    method: 'GET',
    path: '/dashboard/me',
    summary: 'Personal dashboard',
    permission: 'analytics.readOwn',
    auth: true,
    response: c.single(s.dashboardPayload),
  },
  'dashboard.scoped': {
    method: 'GET',
    path: '/dashboard/scoped',
    summary: 'Team or department dashboard',
    permission: 'analytics.readScoped',
    scope: 'reports',
    auth: true,
    request: { query: s.analyticsQuery },
    response: c.single(s.dashboardPayload),
  },
  'dashboard.executive': {
    method: 'GET',
    path: '/dashboard/executive',
    summary: 'Organization-wide dashboard',
    permission: 'analytics.readExecutive',
    auth: true,
    request: { query: s.analyticsQuery },
    response: c.single(s.dashboardPayload),
  },
  'analytics.attendance': {
    method: 'GET',
    path: '/analytics/attendance',
    summary: 'Attendance analytics',
    permission: 'analytics.readScoped',
    scope: 'reports',
    auth: true,
    request: { query: s.analyticsQuery },
    response: c.single(s.dashboardPayload),
  },
  'analytics.leave': {
    method: 'GET',
    path: '/analytics/leave',
    summary: 'Leave analytics',
    permission: 'analytics.readScoped',
    scope: 'reports',
    auth: true,
    request: { query: s.analyticsQuery },
    response: c.single(s.dashboardPayload),
  },
  'analytics.tasks': {
    method: 'GET',
    path: '/analytics/tasks',
    summary: 'Task analytics',
    permission: 'analytics.readScoped',
    scope: 'reports',
    auth: true,
    request: { query: s.analyticsQuery },
    response: c.single(s.dashboardPayload),
  },
  'settings.company.get': {
    method: 'GET',
    path: '/settings/company',
    summary: 'Company settings',
    permission: 'settings.read',
    auth: true,
    response: c.single(s.companySettings),
    notes: 'Secrets are masked.',
  },
  'settings.company.update': {
    method: 'PATCH',
    path: '/settings/company',
    summary: 'Update company settings',
    permission: 'settings.configure',
    auth: true,
    request: { body: z.object({ organization: s.organizationSettings.partial().optional(), systemPreferences: s.systemPreferences.partial().optional() }) },
    response: c.single(s.companySettings),
    audit: 'settings.companyUpdated',
  },
  'settings.email.update': {
    method: 'PATCH',
    path: '/settings/email',
    summary: 'Update SMTP settings',
    permission: 'settings.configureEmail',
    auth: true,
    request: { body: s.updateEmailSettingsBody },
    response: c.single(s.emailSettings),
    audit: 'settings.emailUpdated',
    notes:
      'Admin and Super Admin only — never HR or the CEO. The password is encrypted at rest, ' +
      'and a save FAILS LOUDLY if the encryption key is absent.',
  },
  'settings.email.test': {
    method: 'POST',
    path: '/settings/email/test',
    summary: 'Send a test email',
    permission: 'settings.configureEmail',
    auth: true,
    request: { body: s.testEmailBody },
    response: c.ok,
    audit: 'settings.emailTested',
  },
  'roles.matrix': {
    method: 'GET',
    path: '/roles',
    summary: 'The permission matrix',
    permission: 'roles.read',
    auth: true,
    response: c.single(s.permissionMatrix),
    notes: 'READ-ONLY. Roles are code, not runtime configuration.',
  },
  'audit.list': {
    method: 'GET',
    path: '/audit-logs',
    summary: 'Audit trail',
    permission: 'audit.read',
    auth: true,
    request: { query: s.auditListQuery },
    response: c.paginated(s.auditEntry),
  },
  'audit.export': {
    method: 'GET',
    path: '/audit-logs/export',
    summary: 'Export the audit trail',
    permission: 'audit.export',
    auth: true,
    request: { query: s.auditListQuery },
    response: c.empty,
    audit: 'audit.exported',
    notes: 'Super Admin only.',
  },
  'portalActivity.list': {
    method: 'GET',
    path: '/admin/portal-activity',
    summary: 'Portal sign-ins and estimated time in the web app',
    permission: 'audit.read',
    scope: 'system',
    auth: true,
    request: { query: s.portalActivityListQuery },
    response: z.object({
      data: s.portalActivityPayload,
      pagination: c.pagination,
    }),
    notes:
      'Duration is derived from refresh-token activity (session families), not page tracking.',
  },
});

// ── Support tickets ─────────────────────────────────────────────────────────────

const supportContracts = define({
  'support.tickets.list': {
    method: 'GET',
    path: '/support/tickets',
    summary: 'Support tickets (mine, technical queue or HR queue)',
    permission: 'support.tickets.read',
    scope: 'own',
    auth: true,
    request: { query: sup.supportTicketListQuery },
    response: c.paginated(sup.supportTicket),
  },
  'support.tickets.create': {
    method: 'POST',
    path: '/support/tickets',
    summary: 'Open a support ticket',
    permission: 'support.tickets.create',
    scope: 'own',
    auth: true,
    request: { body: sup.createSupportTicketBody },
    response: c.single(sup.supportTicket),
    audit: 'support.ticketCreated',
  },
  'support.tickets.get': {
    method: 'GET',
    path: '/support/tickets/:id',
    summary: 'One support ticket with messages',
    permission: 'support.tickets.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.single(sup.supportTicket),
  },
  'support.tickets.updateStatus': {
    method: 'PATCH',
    path: '/support/tickets/:id/status',
    summary: 'Staff: change ticket status or close',
    permission: 'support.tickets.manageHr',
    auth: true,
    request: { params: c.idParam, body: sup.updateSupportTicketStatusBody },
    response: c.single(sup.supportTicket),
    audit: 'support.ticketStatusUpdated',
  },
  'support.tickets.addMessage': {
    method: 'POST',
    path: '/support/tickets/:id/messages',
    summary: 'Add a message to a ticket',
    permission: 'support.tickets.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam, body: sup.createSupportMessageBody },
    response: c.single(sup.supportTicket),
    audit: 'support.ticketMessageAdded',
  },
  'support.tickets.employeeResponse': {
    method: 'POST',
    path: '/support/tickets/:id/employee-response',
    summary: 'Employee: confirm resolution, reopen or leave feedback',
    permission: 'support.tickets.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam, body: sup.supportEmployeeResponseBody },
    response: c.single(sup.supportTicket),
    audit: 'support.ticketEmployeeResponse',
  },
  'support.tickets.uploadAttachment': {
    method: 'POST',
    path: '/support/tickets/attachment-files',
    summary: 'Upload a reference file for a ticket',
    permission: 'support.tickets.create',
    scope: 'own',
    auth: true,
    upload: true,
    response: c.single(sup.supportAttachment),
    audit: 'support.ticketAttachmentUploaded',
  },
  'support.tickets.downloadAttachment': {
    method: 'GET',
    path: '/support/tickets/attachment-files/:fileId/download',
    summary: 'Download a ticket attachment',
    permission: 'support.tickets.read',
    scope: 'own',
    auth: true,
    request: { params: c.idParam },
    response: c.empty,
    audit: 'support.ticketAttachmentDownloaded',
  },
  'support.notifications.get': {
    method: 'GET',
    path: '/support/notification-settings',
    summary: 'Alert emails for new technical and HR tickets',
    permission: 'support.notifications.configure',
    auth: true,
    response: c.single(sup.supportNotificationSettings),
  },
  'support.notifications.update': {
    method: 'PATCH',
    path: '/support/notification-settings',
    summary: 'Update support alert emails',
    permission: 'support.notifications.configure',
    auth: true,
    request: { body: sup.updateSupportNotificationSettingsBody },
    response: c.single(sup.supportNotificationSettings),
    audit: 'support.notificationsUpdated',
  },
});

// ── The registry ────────────────────────────────────────────────────────────────

export const CONTRACTS = {
  ...systemContracts,
  ...authContracts,
  ...employeeContracts,
  ...departmentContracts,
  ...attendanceContracts,
  ...leaveContracts,
  ...approvalContracts,
  ...supportContracts,
  ...taskContracts,
  ...workReportContracts,
  ...workSessionContracts,
  ...notificationContracts,
  ...announcementContracts,
  ...recordContracts,
  ...adminContracts,
} satisfies Record<string, EndpointContract>;

export type ContractId = keyof typeof CONTRACTS;

export const CONTRACT_IDS = Object.keys(CONTRACTS) as ContractId[];

export function contract<K extends ContractId>(key: K): (typeof CONTRACTS)[K] {
  return CONTRACTS[key];
}

/** Resolve `:param` placeholders — used by the client and the mock server alike. */
export function buildPath(
  path: string,
  params: Record<string, string | number> = {},
): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_match, name: string) => {
    const value = params[name];
    if (value === undefined) {
      throw new Error(`Missing path parameter "${name}" for "${path}"`);
    }
    return encodeURIComponent(String(value));
  });
}

/** Inferred request and response types, so neither side hand-writes them. */
export type RequestBody<K extends ContractId> = (typeof CONTRACTS)[K] extends {
  request: { body: infer B extends z.ZodTypeAny };
}
  ? z.infer<B>
  : never;

export type RequestQuery<K extends ContractId> = (typeof CONTRACTS)[K] extends {
  request: { query: infer Q extends z.ZodTypeAny };
}
  ? z.input<Q>
  : never;

export type ResponseOf<K extends ContractId> = z.infer<(typeof CONTRACTS)[K]['response']>;
